/**
 * Sozlamalar's own menu, 1:1 with K3's `SEC`: eighteen sections in thirteen groups, and a group
 * earns a heading only when it holds more than one visible section. A branch manager sees the six
 * the design gives them. The router gates each route by the same role, so a hidden link and a
 * refused page cannot disagree.
 */
import type { TranslationKey } from "@/i18n";
import type { Role } from "@/api/types";

export interface SettingsNavItem {
  key: string;
  labelKey: TranslationKey;
  groupKey: TranslationKey;
  to: string;
  roles: readonly Role[];
}

const OWNER: readonly Role[] = ["owner"];
const MANAGER_AND_OWNER: readonly Role[] = ["branch_manager", "owner"];

export const SETTINGS_NAV: readonly SettingsNavItem[] = [
  { key: "agency", labelKey: "soz.nav.agency", groupKey: "soz.group.agency", to: "/sozlamalar/agentlik", roles: OWNER },
  { key: "billing", labelKey: "soz.nav.billing", groupKey: "soz.group.agency", to: "/sozlamalar/tarif", roles: OWNER },
  { key: "funnel", labelKey: "soz.nav.funnel", groupKey: "soz.group.agency", to: "/sozlamalar/voronka", roles: OWNER },
  { key: "team", labelKey: "soz.nav.team", groupKey: "soz.nav.team", to: "/sozlamalar/jamoa", roles: MANAGER_AND_OWNER },
  { key: "distribution", labelKey: "soz.nav.distribution", groupKey: "soz.group.distribution", to: "/sozlamalar/taqsimlash", roles: MANAGER_AND_OWNER },
  { key: "channels", labelKey: "soz.nav.channels", groupKey: "soz.nav.channels", to: "/sozlamalar/kanallar", roles: OWNER },
  { key: "operators", labelKey: "soz.nav.operators", groupKey: "soz.nav.operators", to: "/sozlamalar/turoperatorlar", roles: OWNER },
  { key: "catalog", labelKey: "soz.nav.catalog", groupKey: "soz.nav.operators", to: "/sozlamalar/katalog", roles: OWNER },
  { key: "pricing", labelKey: "soz.nav.pricing", groupKey: "soz.nav.operators", to: "/sozlamalar/narx", roles: OWNER },
  { key: "ai", labelKey: "soz.nav.ai", groupKey: "soz.group.ai", to: "/ai/sozlamalar", roles: MANAGER_AND_OWNER },
  { key: "documents", labelKey: "soz.nav.documents", groupKey: "soz.group.ai", to: "/sozlamalar/hujjatlar", roles: MANAGER_AND_OWNER },
  { key: "templates", labelKey: "soz.nav.templates", groupKey: "soz.nav.templates", to: "/shablonlar", roles: MANAGER_AND_OWNER },
  { key: "finance", labelKey: "soz.nav.finance", groupKey: "soz.group.finance", to: "/sozlamalar/moliya", roles: OWNER },
  { key: "sla", labelKey: "soz.nav.sla", groupKey: "soz.nav.sla", to: "/sozlamalar/sla", roles: MANAGER_AND_OWNER },
  { key: "notifications", labelKey: "soz.nav.notifications", groupKey: "soz.nav.sla", to: "/sozlamalar/bildirishnomalar", roles: OWNER },
  { key: "import", labelKey: "soz.nav.import", groupKey: "soz.nav.import", to: "/sozlamalar/import", roles: OWNER },
  { key: "audit", labelKey: "soz.nav.audit", groupKey: "soz.group.audit", to: "/sozlamalar/audit", roles: OWNER },
  { key: "system", labelKey: "soz.nav.system", groupKey: "soz.group.audit", to: "/sozlamalar/tizim", roles: OWNER },
];

/** TZ §17.14: the moliyachi's two screens, outside K3's owner menu. */
export const MOLIYACHI_SETTINGS_NAV: readonly SettingsNavItem[] = [
  { key: "finance", labelKey: "soz.nav.finance", groupKey: "soz.group.finance", to: "/sozlamalar/moliya", roles: ["moliyachi"] },
  { key: "billing", labelKey: "soz.nav.billing", groupKey: "soz.group.agency", to: "/sozlamalar/tarif", roles: ["moliyachi"] },
];

/** The sections a role may open. An empty list means the role has no settings at all. */
export function settingsNavFor(role: Role | undefined): readonly SettingsNavItem[] {
  if (role === undefined) {
    return [];
  }
  return [...SETTINGS_NAV, ...MOLIYACHI_SETTINGS_NAV].filter((item) => item.roles.includes(role));
}

/** A list in order with the group headings only where a group holds more than one section. */
export type SettingsNavLine = { heading: TranslationKey } | { item: SettingsNavItem };

export function settingsNavLines(items: readonly SettingsNavItem[]): SettingsNavLine[] {
  const lines: SettingsNavLine[] = [];
  items.forEach((item, index) => {
    const first = index === 0 || items[index - 1]?.groupKey !== item.groupKey;
    if (first && items.filter((other) => other.groupKey === item.groupKey).length > 1) {
      lines.push({ heading: item.groupKey });
    }
    lines.push({ item });
  });
  return lines;
}

/**
 * Older links name a section rather than a route: `?section=channels` from the Meta callback and
 * the owner's alerts, `?s=tizim` from Bosh sahifa. Both still land on the right screen.
 */
const LEGACY_SECTION: Readonly<Record<string, string>> = {
  ag: "agency",
  jamoa: "team",
  taqsim: "distribution",
  kanal: "channels",
  channels: "channels",
  ops: "operators",
  moliya: "finance",
  finance: "finance",
  shablon: "templates",
  documents: "templates",
  hujjat: "documents",
  bild: "notifications",
  notifications: "notifications",
  import: "import",
  tizim: "system",
  system: "system",
  audit: "audit",
  sla: "sla",
  pipeline: "funnel",
  team: "team",
  agency: "agency",
};

export function legacySectionTarget(
  items: readonly SettingsNavItem[],
  params: URLSearchParams,
): SettingsNavItem | undefined {
  const name = params.get("section") ?? params.get("s");
  const key = name === null ? undefined : LEGACY_SECTION[name];
  return items.find((item) => item.key === key) ?? items[0];
}
